import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  MenuItem,
  Alert,
  Typography,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const SalePaymentEdit = ({ open, onClose, editData, token, onSuccess }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/customer-balance-payments",
    token
  );

  const { data: shopsData, isLoading: shopLoading } = useFetchObjects(
    ["shop", "sale-payment-edit"],
    "shop/shop",
    token
  );
  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const [selectedShop, setSelectedShop] = useState("");

  const { data: customersData, loading: customersLoading } = useFetchObjects(
    ["customers", "sale-payment-edit", selectedShop],
    selectedShop ? `shop/customers?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const { data: salesData } = useFetchObjects(
    ["sales", "sale-payment-edit", selectedShop],
    selectedShop ? `shop/sales/?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency", "sale-payment-edit", selectedShop],
    selectedShop ? `shop/currencies?page_size=all&shop=${selectedShop}` : "",
    token
  );

  const customers = customersData?.results || customersData || [];
  const sales = salesData?.results || salesData || [];
  const currencies = getUniqueCurrencies(currenciesData?.results || currenciesData || []);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [discount, setDiscount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [notes, setNotes] = useState("");
  const initialRateSelectionRef = useRef("");
  const customerOptions = useMemo(() => {
    const base = selectedShop ? [...(customers || [])] : [];
    const virtuals = [];
    const seen = new Set(base.map((c) => (c.name || "").trim().toLowerCase()).filter(Boolean));
    (sales || []).forEach((s) => {
      if (!s?.customer && (s?.general_customer_name || s?.general_customer_phone)) {
        const name = (s.general_customer_name || "General Customer").trim();
        const phone = (s.general_customer_phone || "").trim();
        const key = name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          virtuals.push({
            id: `general-customer::${selectedShop}::${name}::${phone}`,
            name,
          });
        }
      }
    });
    return [...virtuals, ...base];
  }, [selectedShop, customers, sales]);

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "sale-payment-edit", selectedShop],
    selectedShop ? `shop/accounts/?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];

  const selectedCurrencyId = selectedCurrency?.id ? String(selectedCurrency.id) : "";
  const shopAccounts = useMemo(() => accounts, [accounts]);
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((a) => String(a.currency) === String(selectedCurrencyId)),
    [accounts, selectedCurrencyId]
  );
  const selectedAccount = useMemo(
    () =>
      shopAccounts.find((a) => String(a.id) === String(selectedAccountId)) ||
      null,
    [shopAccounts, selectedAccountId]
  );

  useEffect(() => {
    if (editData) {
      setAmount(editData.amount ?? "");
      setDiscount(editData.discount ?? "");
      setExchangeRate(editData.exchange_rate || "");
      setPaymentDate(
        editData.payment_date ? editData.payment_date.slice(0, 16) : ""
      );
      setNotes(editData.notes || "");
      if (editData.shop) setSelectedShop(String(editData.shop));
      if (editData.customer_details) {
        setSelectedCustomer({
          id: editData.customer_details.id,
          name: editData.customer_details.name,
        });
      } else if (editData.customer) {
        const c = customers.find((x) => x.id === editData.customer);
        if (c) setSelectedCustomer(c);
      }
      if (editData.currency_details) {
        setSelectedCurrency({
          id: editData.currency_details.id,
          title: editData.currency_details.title,
        });
      } else if (editData.currency && currencies.length) {
        const curr = currencies.find((x) => x.id === editData.currency);
        if (curr) setSelectedCurrency(curr);
      }
      const accountId =
        editData.account_details?.id
          ? String(editData.account_details.id)
          : editData.account
            ? String(editData.account)
            : "";
      setSelectedAccountId(accountId);
      const currencyId = editData.currency_details?.id
        ? String(editData.currency_details.id)
        : editData.currency
          ? String(editData.currency)
          : "";
      initialRateSelectionRef.current = `${currencyId}:${accountId}`;
    }
  }, [editData, customers, currencies]);

  useEffect(() => {
    if (!selectedCurrencyId) {
      setSelectedAccountId("");
      return;
    }
    if (shopAccounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = shopAccounts.some((a) => String(a.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || shopAccounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrencyId, shopAccounts, sameCurrencyAccounts, selectedAccountId]);

  const { data: directRateData } = useFetchObjects(
    [
      "sale-payment-edit-rate-direct",
      selectedShop,
      selectedCurrencyId,
      selectedAccount?.currency,
    ],
    selectedShop && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShop}&from_currency=${selectedCurrencyId}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    [
      "sale-payment-edit-rate-reverse",
      selectedShop,
      selectedCurrencyId,
      selectedAccount?.currency,
    ],
    selectedShop && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShop}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrencyId}`
      : "",
    token
  );

  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrencyId) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrencyId;
    if (String(selectedCurrencyId) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrencyId, selectedAccount, directRateData, reverseRateData]);

  const convertedAmountPreview = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  const discountValue = Number(discount) || 0;
  const amountValue = Number(amount) || 0;
  const totalReducePreview =
    Number.isFinite(amountValue) && amountValue > 0
      ? (amountValue + Math.max(0, discountValue)).toFixed(2)
      : "";

  useEffect(() => {
    if (!editData || !exchangeRatePreview) return;
    const currentRateSelection = `${selectedCurrencyId}:${selectedAccountId}`;
    if (currentRateSelection !== initialRateSelectionRef.current) {
      setExchangeRate(exchangeRatePreview);
    }
  }, [editData, exchangeRatePreview, selectedCurrencyId, selectedAccountId]);

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
      onClose();
    }
  }, [isSuccess, onClose, onSuccess]);

  const handleSubmit = () => {
    if (
      !editData?.id ||
      !selectedShop ||
      !selectedCustomer?.id ||
      !selectedCurrency?.id ||
      !amount ||
      !exchangeRate
    )
      return;
    const payload = {
      customer: selectedCustomer.id,
      currency: selectedCurrency.id,
      amount: Number(amount),
      discount: Math.max(0, Number(discount) || 0),
      exchange_rate: exchangeRate,
      payment_date: paymentDate,
      notes: notes.trim() || undefined,
    };
    if (selectedAccountId) payload.account = selectedAccountId;
    handleUpdate(editData.id, payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("salePayment.editPayment", "Edit payment")}
      </DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("salePayment.selectShop", "Select branch")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => {
              setSelectedShop(e.target.value);
              setSelectedCustomer(null);
              setSelectedCurrency(null);
              setSelectedAccountId("");
            }}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shops.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <Autocomplete
            value={selectedCustomer}
            onChange={(e, newValue) => setSelectedCustomer(newValue)}
            options={customerOptions}
            getOptionLabel={(option) => option?.name ?? "—"}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={customersLoading}
            disabled={!selectedShop}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("salePayment.customer", "Customer")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customersLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Autocomplete
            value={selectedCurrency}
            onChange={(e, newValue) => {
              setSelectedCurrency(newValue);
              setSelectedAccountId("");
            }}
            options={selectedShop ? currencies || [] : []}
            getOptionLabel={(option) => option?.title ?? ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={currencyLoading}
            disabled={!selectedShop}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("salePayment.selectCurrency", "Currency")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {currencyLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            select
            margin="dense"
            label={t("salePayment.account", "Account")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!selectedShop || !selectedCurrencyId || accountLoading}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : shopAccounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("salePayment.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              shopAccounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("salePayment.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            helperText={t(
              "salePayment.amountHelp",
              "Cash received from customer"
            )}
          />

          <TextField
            margin="dense"
            label={t("salePayment.discount", "Discount")}
            type="number"
            fullWidth
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            helperText={t(
              "salePayment.discountHelp",
              "Extra amount forgiven from customer balance (not cash)"
            )}
          />

          {totalReducePreview && (
            <Box sx={{ px: 0.5, mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t("salePayment.totalBalanceReduce", "Total balance reduce")}:{" "}
                <strong>
                  {totalReducePreview}
                  {selectedCurrency?.title ? ` ${selectedCurrency.title}` : ""}
                </strong>
                {" "}
                ({t("salePayment.amount", "Amount")}
                {discountValue > 0
                  ? ` + ${t("salePayment.discount", "Discount")}`
                  : ""}
                )
              </Typography>
            </Box>
          )}

          <TextField
            margin="dense"
            label={t("salePayment.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            inputProps={{ min: 0, step: "0.000001" }}
          />

          <TextField
            margin="dense"
            label={t("salePayment.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmountPreview}
            disabled
          />

          {!exchangeRatePreview && selectedCurrencyId && selectedAccountId && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t("salePayment.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
            </Alert>
          )}
          <TextField
            margin="dense"
            label={t("salePayment.paymentDate", "Payment date")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("salePayment.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("salePayment.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !selectedCustomer ||
            !selectedCurrency ||
            !amount ||
            !exchangeRate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("salePayment.submit", "Save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalePaymentEdit;
